import React, { useEffect, useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { Menu, X } from "lucide-react";

const NavLink = ({ href, children, onClick, testid }) => (
  <a
    href={href}
    onClick={onClick}
    data-testid={testid}
    className="text-sm font-medium tracking-wide text-ink-800 hover:text-crimson transition-colors duration-200"
  >
    {children}
  </a>
);

export default function Header() {
  const { lang, setLang, t, languages } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { href: "#about", label: t.nav.about, id: "nav-about" },
    { href: "#practice", label: t.nav.practice, id: "nav-practice" },
    { href: "#team", label: t.nav.team, id: "nav-team" },
    { href: "#contact", label: t.nav.contact, id: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full bg-cream-100/90 backdrop-blur-md border-b transition-all duration-300 ${
        scrolled ? "border-ink-800/15" : "border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <a
            href="#top"
            data-testid="logo-link"
            className="flex items-baseline gap-2 group"
          >
            <span className="font-serif text-2xl font-semibold tracking-tight text-ink-800 group-hover:text-crimson transition-colors">
              LAWSN
            </span>
            <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500">
              Serkedjiev Law Office
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((n) => (
              <NavLink key={n.href} href={n.href} testid={n.id}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div
              role="group"
              aria-label="Language"
              data-testid="language-switcher"
              className="flex items-center gap-0 border border-ink-800/20"
            >
              {languages.map((l) => {
                const active = l.code === lang;
                return (
                  <button
                    key={l.code}
                    type="button"
                    data-testid={`lang-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={`px-2.5 py-1 font-mono text-[11px] tracking-widest transition-colors ${
                      active
                        ? "bg-ink-800 text-cream-100"
                        : "text-ink-800 hover:bg-ink-800/5"
                    }`}
                  >
                    {l.label}
                  </button>
                );
              })}
            </div>

            <a
              href="#contact"
              data-testid="cta-header-button"
              className="hidden md:inline-flex items-center px-4 py-2 border border-ink-800 bg-ink-800 text-cream-100 hover:bg-crimson hover:border-crimson transition-colors text-sm font-medium"
            >
              {t.nav.cta}
            </a>

            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden p-2 -mr-2 text-ink-800"
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {open && (
          <div data-testid="mobile-menu" className="md:hidden border-t border-ink-800/10 py-4">
            <nav className="flex flex-col gap-4">
              {navItems.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  data-testid={`mobile-${n.id}`}
                  onClick={() => setOpen(false)}
                  className="font-medium text-ink-800 hover:text-crimson transition-colors"
                >
                  {n.label}
                </a>
              ))}
              <a
                href="#contact"
                data-testid="cta-mobile-button"
                onClick={() => setOpen(false)}
                className="inline-flex items-center w-fit mt-2 px-4 py-2 border border-ink-800 bg-ink-800 text-cream-100 text-sm"
              >
                {t.nav.cta}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
